import { Injectable, Inject } from '@angular/core';
import * as _ from 'lodash';

import { UserService } from '../../services/core.user.service';
import { RefusedError } from '../../services/core.refused-error';
import { TourService } from '../tour/tour.service';
import { ProjectService } from '../../services/project.service';
import { ProjectModel } from '../../services/project.model.service';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { WikiHelperService } from '../../../widgets/wiki/services/wiki.helper';
import { UiService } from '../../services/core.ui.service';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../services/core.translate.service';
import { FeatureFlagService } from '../../services/core.feature-flags.service';

@Injectable()
export class ProjectAdminResolvesService {

    constructor(
        private userService: UserService,
        private tourService: TourService,
        private projectService: ProjectService,
        private projectModel: ProjectModel,
        private wikiService: WikiService,
        private wikiHelper: WikiHelperService,
        private uiService: UiService,
        private stateService: StateService,
        private translateService: GfTranslateService,
        private featureFlagService: FeatureFlagService
    ) {
    }

    async adminSecurity(transition: any, isProjectAdmin: any): Promise<void> {
        if (! this.userService.session.hasUser()) {
            throw RefusedError.unauthorized('Project admin area requires a session');
        }

        if (isProjectAdmin) return;
    
        // Authorize tracker admin states
        const trackerRoutes = /home.project.summary.admin.tracker/;
        if (transition.to().name.match(trackerRoutes) ) {
            const isTrackerAdmin = await this.projectModel.isTrackerAdmin();
            if (!isTrackerAdmin) {
                throw RefusedError.forbidden('User is not a tracker admin');
            }
            return;
        }

        throw RefusedError.forbidden('User is not a project admin');
    }

    adminRoles(unixName: string): Promise<any> {
        return this.projectService.roles(unixName);
    }

    async adminModules(unixName: string): Promise<any[]> {
        try {
            const response = await this.projectService.modules(unixName);
            return _.get(response, 'items', []);
        } catch (response) {
            throw response.status;
        }
    }

    adminOrgsTour() {
        return this.tourService.setTour('projectAdminOrgsCtrl');
    }

    async adminUseCustomSplash(project: any): Promise<string> {
        try {
            const response = await this.projectService.getUseCustomSplash(project.id);
            return _.get(response, 'settingValue', 'N');
        } catch (error) {
            return 'N';
        }
    }

    async adminHomepageWikiPage(unixName: string, pageName: string = 'FrontPage'): Promise<any> {
        const id = pageName || 'FrontPage';

        if (id == '') return false;

        try {
            const data = await this.wikiService.info(encodeURIComponent(id), {
                rel: 'monitor,options,version,content',
                project: unixName
            });
            return data;
        } catch (error) {
            const wikiPage = { pagename: id, rel: { content: { data: null } } };
            wikiPage.rel.content.data = this.wikiHelper.getContents(wikiPage);
            return wikiPage;
        }
    }

    adminHomepageWikiHtml(wikiPage: any, unixName: string) {
        const urlFn = (pagename) => {
            const ret = this.stateService.href(
                'home.project.summary.wiki.page',
                { unixName: unixName, pagename: pagename }
            );
            return ret;
        };

        const wikiContent = _.get(wikiPage, 'rel.content.data', '');

        return this.wikiHelper.convertWikiWords(
            wikiContent,
            urlFn
        );
    }

    adminHomepageTour(transition: any) {
        // When a state has children, you have to avoid
        // inheriting the tour setting.
        if (transition.to().name == 'home.project.summary.admin.homepage') {
            return this.tourService.setTour('projectAdminHPCtrl');
        }
    }

    adminHomepageEditEnabled(useCustomSplash: any) {
        if (useCustomSplash != 'Y') {
            this.uiService.message.showWarning(
                this.translateService.translate('This project is using the Standard homepage.'),
                this.translateService.translate('Project Homepage')
            );
            this.stateService.go('home.project.summary.admin.homepage');
        }
    }

    adminScmTour() {
        return this.tourService.setTour('projectAdminSCMCtrl');
    }

    async adminScmAcl(unixName: string): Promise<any[]> {
        try {
            const data = await this.projectService.getAcl(unixName);
            return _.get(data, 'items', []);
        } catch (response) {
            throw response.status;
        }
    }

    async adminScmRoles(unixName: string): Promise<any[]> {
        try {
            const data = await this.projectService.getSectionRoleSettings(
                unixName,
                'scm'
            );
            return _.get(data, 'items', []);
        } catch (response) {
            throw response.status;
        }
    }

    async adminScm(unixName: string): Promise<any> {
        try {
            const response = await this.projectService.getScm(unixName);
            return response;
        } catch (response) {
            throw response.status;
        }
    }

    adminScmAclTour() {
        return this.tourService.setTour('projectAdminScmAclCtrl');
    }

    adminTrackerTour() {
        return this.tourService.setTour('projectAdminTrackerCtrl');
    }

    adminRolesTour() {
        return this.tourService.setTour('projectAdminRolesCtrl');
    }

    adminGroups(unixName: string, stateParams: any) {
        return this.projectService.getGroups(
            unixName,
            stateParams
        );
    }

    adminGroupsTour() {
        return this.tourService.setTour('projectAdminGroupsCtrl');
    }

    adminEditTour() {
        return this.tourService.setTour('projectAdminEditCtrl');
    }

    adminSnapshotsTour() {
        return this.tourService.setTour('projectAdminSnapCtrl');
    }

    adminTroveTour() {
        return this.tourService.setTour('projectAdminCategoriesCtrl');
    }

    requireFlowFeature(): void {
        if (!this.featureFlagService.isFeatureEnabled('flow')) {
            this.stateService.go('home.not-found');
            throw new Error('Feature not available');
        }
    }
}
